import React, { useState, useMemo, useEffect } from "react"
import { Row, Col, Table, Modal, Form } from "reactstrap"
import Select from "react-select"
// assets
import Loader from "pages/Separate/Loader"
import AccessDeniedPopup from "routes/AccessDeniedPopup"
import leaveImg2 from "../../../assets/images/LeaveDetails.jpg"
import { fetchData, FetchPostData } from "utils/common-fetchData"
import Swal from "sweetalert2"
import { getInitials } from "utils/common-helpers"
import { colourStyles } from "utils/common-options"
import configure from "configure"
import moment from "moment"
import "./Leaveapprovals.css"
import LeaveStatsSummary from "pages/HumanResources/Payroll Management/LeaveManagement/LeaveStatsSummary"

const AVATAR_COLORS = [
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#ffe4d5", color: "#c2410c" },
  { bg: "#d1fae5", color: "#047857" },
  { bg: "#ede9fe", color: "#5b21b6" },
  { bg: "#fce7f3", color: "#be185d" },
  { bg: "#fef9c3", color: "#854d0e" },
  { bg: "#e2e8f0", color: "#334155" },
]

// Keep in sync with the color tokens (--green-*, --blue-*, etc.)
// defined in Leaveapprovals.css
const STATUS_STYLES = {
  approved: { bg: "#e8f7ee", fg: "#087a42" },
  applied: { bg: "#eaf2ff", fg: "#245fbd" },
  pending: { bg: "#fff3df", fg: "#a95709" },
  unapproved: { bg: "#fbe9e9", fg: "#c62b2b" },
  disapproved: { bg: "#fbe9e9", fg: "#c62b2b" },
  cancel: { bg: "#eef1f4", fg: "#5a6472" },
  cancelled: { bg: "#eef1f4", fg: "#5a6472" },
  default: { bg: "#eef1f4", fg: "#5a6472" },
}

// Slot type ids (tblleave_slots.type): 1 = Sick, 2 = Earned, 3 = Casual.
// NOTE: Casual Leave is discontinued — it is never shown in the UI
// (not in the panel, not in View, not in Apply). Type 3 is only kept
// in backend logic so historical records still aggregate correctly.
const LEAVE_TYPE_NAMES = {
  1: "Sick Leave",
  2: "Earned Leave",
  3: "Casual Leave",
}

const isCasualType = name =>
  (name || "").toString().trim().toLowerCase().includes("casual")

const getStatusStyle = status => {
  const key = (status || "").toString().trim().toLowerCase()
  return STATUS_STYLES[key] || STATUS_STYLES.default
}

const StatusBadge = ({ status }) => {
  const style = getStatusStyle(status)
  return (
    <span
      className="la-badge"
      style={{
        background: style.bg,
        color: style.fg,
        borderColor: `${style.fg}30`,
      }}
    >
      {status || "-"}
    </span>
  )
}

const getAvatarColor = key => {
  const str = (key ?? "").toString()
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const formatLeaveDate = (start, end) => {
  const s = start ? moment(start).format("DD-MM-YYYY") : null
  const e = end ? moment(end).format("DD-MM-YYYY") : null
  if (!s && !e) return "-"
  return s && e && s !== e ? `${s} - ${e}` : s || e
}

const formatShortDate = value => {
  if (!value) return "-"
  const m = moment(value)
  return m.isValid() ? m.format("DD MMM YYYY") : "-"
}

/* Row-level helpers — approvalList rows use slightly different field names
   across modules, so read every known alias and fall back gracefully.
   approvalList only returns pending queue rows (tblleave_slots.status = 0),
   so when no explicit text status exists the row is still "Pending". */
const isMeaningfulLabel = value => {
  if (value === null || value === undefined) return false
  const str = value.toString().trim()
  if (!str) return false
  return Number.isNaN(Number(str)) // pure numbers like "0"/"1" are flags, not labels
}

const firstMeaningfulToken = value => {
  if (value === null || value === undefined) return ""
  return value
    .toString()
    .split(",")
    .map(part => part.trim())
    .find(part => isMeaningfulLabel(part)) || ""
}

const mapLeaveType = value => {
  if (value === null || value === undefined || value === "") return ""
  const str = value.toString().trim()
  if (!str) return ""
  if (LEAVE_TYPE_NAMES[str]) return LEAVE_TYPE_NAMES[str]
  return isMeaningfulLabel(str) ? str : ""
}

const getRowType = row => {
  // New backend field: comma-separated distinct type names for the request
  if (row?.leave_types && isMeaningfulLabel(row.leave_types)) {
    return row.leave_types
  }
  return (
    mapLeaveType(row?.leave_name) ||
    mapLeaveType(row?.sub_leave_name) ||
    mapLeaveType(row?.leave_type_name) ||
    mapLeaveType(row?.type_name) ||
    mapLeaveType(row?.leave_type) ||
    mapLeaveType(row?.type) ||
    mapLeaveType(row?.leave) ||
    ""
  )
}

const getRowStatus = row => {
  const candidates = [
    row?.leave_status,
    row?.status_text,
    row?.approval_status,
  ]
  for (const c of candidates) {
    const token = firstMeaningfulToken(c)
    if (token) return token
  }
  // Pending queue has no text status yet — show Pending instead of "—"
  // so the column always carries a color-coded badge.
  return "Pending"
}

const getRowAppliedDate = row => row?.created_at || row?.applied_on || null

const getRowStartDate = row => row?.start_date || row?.from_date || null

const getRowEndDate = row => row?.end_date || row?.to_date || null

/* ---------------------------------------------------------------- */
/* Pagination bar — numbered, active page in solid purple            */
/* ---------------------------------------------------------------- */
function LaPagination({
  pageIndex,
  pageCount,
  totalRows,
  pageSize,
  gotoPage,
  previousPage,
  nextPage,
  canPreviousPage,
  canNextPage,
}) {
  const from = totalRows === 0 ? 0 : pageIndex * pageSize + 1
  const to = Math.min(totalRows, pageIndex * pageSize + pageSize)

  // Windowed page numbers so very long lists don't render 200+ buttons
  const pages = useMemo(() => {
    const windowSize = 5
    let start = Math.max(0, pageIndex - Math.floor(windowSize / 2))
    let end = Math.min(pageCount, start + windowSize)
    start = Math.max(0, end - windowSize)
    return Array.from({ length: end - start }, (_, i) => start + i)
  }, [pageIndex, pageCount])

  return (
    <div className="la-pagination">
      <span className="la-page-info">
        Showing {from}-{to} of {totalRows}
      </span>
      <button
        type="button"
        className="la-page-btn"
        onClick={previousPage}
        disabled={!canPreviousPage}
        aria-label="Previous page"
      >
        <i className="bx bx-chevron-left" />
      </button>
      {pages.map(p => (
        <button
          key={p}
          type="button"
          className={`la-page-btn${p === pageIndex ? " active" : ""}`}
          onClick={() => gotoPage(p)}
        >
          {p + 1}
        </button>
      ))}
      <button
        type="button"
        className="la-page-btn"
        onClick={nextPage}
        disabled={!canNextPage}
        aria-label="Next page"
      >
        <i className="bx bx-chevron-right" />
      </button>
    </div>
  )
}

/* ---------------------------------------------------------------- */
/* Grouped leaves table                                              */
/* ---------------------------------------------------------------- */
function GroupedLeavesTable({
  data,
  onLeaveDetail,
  filterMode,
  setFilterMode,
  isApprover,
}) {
  const [globalFilter, setGlobalFilter] = useState("")
  const [pageSize, setPageSize] = useState(10)
  const [pageIndex, setPageIndex] = useState(0)
  const [collapsed, setCollapsed] = useState({})

  const searchedData = useMemo(() => {
    if (!globalFilter.trim()) return data

    const term = globalFilter.toLowerCase().trim()

    return data.filter(row =>
      Object.values(row || {}).some(
        value =>
          value !== null &&
          value !== undefined &&
          value.toString().toLowerCase().includes(term)
      )
    )
  }, [data, globalFilter])

  const groups = useMemo(() => {
    const map = new Map()

    searchedData.forEach(row => {
      const key = row.employee ?? row.emp_id ?? row.name ?? "unknown"

      if (!map.has(key)) {
        map.set(key, {
          key,
          emp_id: row.emp_id,
          name: row.name || "Unknown",
          designation: row.designation,
          avatar: row.avatar,
          rows: [],
        })
      }

      map.get(key).rows.push(row)
    })

    return Array.from(map.values())
      .map(group => {
        const sortedRows = [...group.rows].sort(
          (a, b) =>
            new Date(getRowStartDate(b) || 0) -
            new Date(getRowStartDate(a) || 0)
        )

        const totalDays = Number(
          group.rows
            .reduce((sum, row) => sum + (parseFloat(row.leave_days) || 0), 0)
            .toFixed(1)
        )

        // Real aggregates for the summary (parent) row — replaces the
        // previous static "Latest request / Employee leave requests" text.
        const appliedDates = group.rows
          .map(getRowAppliedDate)
          .filter(Boolean)
          .map(d => new Date(d).getTime())
          .filter(t => !Number.isNaN(t))
        const latestApplied = appliedDates.length
          ? new Date(Math.max(...appliedDates))
          : null

        const starts = group.rows
          .map(getRowStartDate)
          .filter(Boolean)
          .map(d => new Date(d).getTime())
          .filter(t => !Number.isNaN(t))
        const ends = group.rows
          .map(getRowEndDate)
          .filter(Boolean)
          .map(d => new Date(d).getTime())
          .filter(t => !Number.isNaN(t))
        const periodStart = starts.length ? new Date(Math.min(...starts)) : null
        const periodEnd = ends.length ? new Date(Math.max(...ends)) : null

        // Leave-type breakdown, e.g. { "Sick Leave": 2, "Earned Leave": 1 }.
        // `leave_types` (new backend field) is a comma-separated list.
        const typeBreakdown = {}
        const addType = t => {
          const name = (t || "").toString().trim()
          if (!name || isCasualType(name)) return
          typeBreakdown[name] = (typeBreakdown[name] || 0) + 1
        }
        group.rows.forEach(row => {
          if (row?.leave_types && isMeaningfulLabel(row.leave_types)) {
            row.leave_types
              .toString()
              .split(",")
              .forEach(part => addType(part))
          } else {
            addType(getRowType(row))
          }
        })

        // Day-wise status totals for the group. Prefers the new backend
        // fields (applied/approved/unapproved/cancelled days); falls back
        // to summing leave_days under the row's text status (Pending for
        // the approval queue) so the parent always shows a badge.
        const statusDays = { Applied: 0, Approved: 0, Unapproved: 0, Cancel: 0 }
        let hasStatusDays = false
        group.rows.forEach(row => {
          const a = parseFloat(row?.applied_days)
          const p = parseFloat(row?.approved_days)
          const u = parseFloat(row?.unapproved_days)
          const c = parseFloat(row?.cancelled_days)
          if (!Number.isNaN(a) || !Number.isNaN(p) || !Number.isNaN(u) || !Number.isNaN(c)) {
            hasStatusDays = true
            statusDays.Applied += Number.isNaN(a) ? 0 : a
            statusDays.Approved += Number.isNaN(p) ? 0 : p
            statusDays.Unapproved += Number.isNaN(u) ? 0 : u
            statusDays.Cancel += Number.isNaN(c) ? 0 : c
          } else {
            const s = getRowStatus(row)
            if (s) {
              hasStatusDays = true
              const raw = s.toLowerCase() === "cancelled" ? "Cancel" : s
              const label =
                raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
              const days = parseFloat(row?.leave_days)
              statusDays[label] =
                (statusDays[label] || 0) + (Number.isNaN(days) ? 1 : days)
            }
          }
        })
        if (!hasStatusDays) {
          Object.keys(statusDays).forEach(k => delete statusDays[k])
        }

        return {
          ...group,
          totalDays,
          latestApplied,
          periodStart,
          periodEnd,
          typeBreakdown,
          statusDays,
          rows: sortedRows,
        }
      })
      .sort((a, b) => b.totalDays - a.totalDays || a.name.localeCompare(b.name))
  }, [searchedData])

  const stats = useMemo(() => {
    const employees = groups.length

    const requests = searchedData.length

    const days = Number(
      searchedData
        .reduce((sum, row) => sum + (parseFloat(row.leave_days) || 0), 0)
        .toFixed(1)
    )

    const avgDays =
      employees > 0 ? Number((days / employees).toFixed(1)) : 0

    return {
      employees,
      requests,
      days,
      avgDays,
    }
  }, [groups, searchedData])

  useEffect(() => {
    setPageIndex(0)
  }, [globalFilter, pageSize, data])

  const pageCount = Math.max(1, Math.ceil(groups.length / pageSize))

  const safePageIndex = Math.min(pageIndex, pageCount - 1)

  const pageGroups = groups.slice(
    safePageIndex * pageSize,
    safePageIndex * pageSize + pageSize
  )

  const toggleGroup = key => {
    setCollapsed(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const expandAll = () => {
    const state = {}

    groups.forEach(group => {
      state[group.key] = false
    })

    setCollapsed(state)
  }

  const collapseAll = () => {
    const state = {}

    groups.forEach(group => {
      state[group.key] = true
    })

    setCollapsed(state)
  }

  const getPageRange = () => {
    if (groups.length === 0) {
      return "0"
    }

    const from = safePageIndex * pageSize + 1

    const to = Math.min(groups.length, safePageIndex * pageSize + pageSize)

    return `${from}-${to}`
  }

  return (
    <div className="leave-approvals">
      {/* Page header */}
      <div className="la-page-header">
        <div className="la-heading-area">
          <div className="la-eyebrow">
            <span className="la-eyebrow-dot" />
            Leave Management
          </div>
          <div className="la-heading-row">
            <div>
              <h1 className="la-title">Leave Approvals</h1>
              <p className="la-subtitle">
                Review employee leave requests and take appropriate action.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="la-kpi-grid">
        <div className="la-kpi-card">
          <div className="la-kpi-icon purple">
            <i className="bx bx-group" />
          </div>
          <div className="la-kpi-content">
            <span className="la-kpi-label">Employees</span>
            <strong className="la-kpi-value">{stats.employees}</strong>
            <span className="la-kpi-helper">Employees with requests</span>
          </div>
        </div>

        <div className="la-kpi-card">
          <div className="la-kpi-icon blue">
            <i className="bx bx-file" />
          </div>
          <div className="la-kpi-content">
            <span className="la-kpi-label">Leave Requests</span>
            <strong className="la-kpi-value">{stats.requests}</strong>
            <span className="la-kpi-helper">Requests in current view</span>
          </div>
        </div>

        <div className="la-kpi-card">
          <div className="la-kpi-icon amber">
            <i className="bx bx-calendar-event" />
          </div>
          <div className="la-kpi-content">
            <span className="la-kpi-label">Leave Days</span>
            <strong className="la-kpi-value">{stats.days}</strong>
            <span className="la-kpi-helper">Total requested days</span>
          </div>
        </div>

        <div className="la-kpi-card la-kpi-card-highlight">
          <div className="la-kpi-icon green">
            <i className="bx bx-check-shield" />
          </div>
          <div className="la-kpi-content">
            <span className="la-kpi-label">Avg Days / Employee</span>
            <strong className="la-kpi-value">{stats.avgDays}</strong>
            <span className="la-kpi-helper">Total days ÷ employees</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="la-toolbar-card">
        <div className="la-toolbar-left">
          <div className="la-show-control">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="la-pagesize"
            >
              {[10, 25, 50, 100].map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>employees</span>
          </div>

          {isApprover && (
            <div className="la-segment">
              <button
                type="button"
                className={filterMode === "default" ? "active" : ""}
                onClick={() => setFilterMode("default")}
              >
                Default
              </button>
              <button
                type="button"
                className={filterMode === "all" ? "active" : ""}
                onClick={() => setFilterMode("all")}
              >
                All
              </button>
            </div>
          )}
        </div>

        <div className="la-toolbar-right">
          <button type="button" className="la-text-action" onClick={expandAll}>
            <i className="bx bx-chevrons-down" />
            Expand all
          </button>
          <button
            type="button"
            className="la-text-action"
            onClick={collapseAll}
          >
            <i className="bx bx-chevrons-up" />
            Collapse
          </button>
          <div className="la-search">
            <i className="bx bx-search" />
            <input
              type="text"
              placeholder="Search employee, ID, type or reason..."
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
            />
            {globalFilter && (
              <button
                type="button"
                className="la-search-clear"
                onClick={() => setGlobalFilter("")}
              >
                <i className="bx bx-x" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grouped leave requests */}
      <div className="la-table-card">
        <div className="la-table-topbar">
          <div>
            <h3>Leave requests</h3>
            <span>
              {groups.length} employee
              {groups.length !== 1 ? "s" : ""} in this view
            </span>
          </div>
          <div className="la-table-status">
            <span className="la-status-dot" />
            Approval queue
          </div>
        </div>

        <div className="la-table-scroll">
          <table className="la-table">
            <thead>
              <tr>
                <th className="la-employee-column">Employee</th>

                <th>Leave period</th>

                <th>Applied on</th>

                <th>Type</th>

                <th>Reason</th>

                <th className="center">Days</th>

                <th className="center">Status</th>

                <th className="center">Action</th>
              </tr>
            </thead>

            {pageGroups.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={8} className="la-empty">
                    <div className="la-empty-icon">
                      <i className="bx bx-search-alt" />
                    </div>

                    <strong>No leave requests found</strong>

                    <span>Try changing your search or filter.</span>
                  </td>
                </tr>
              </tbody>
            )}

            {pageGroups.map(group => {
              const isOpen = !collapsed[group.key]

              const avatarColor = getAvatarColor(group.emp_id || group.name)

              const typeEntries = Object.entries(group.typeBreakdown || {})
              const statusEntries = Object.entries(group.statusDays || {}).filter(
                ([, days]) => Number(days) > 0
              )
              const latestReason = group.rows?.[0]?.leave_reason || ""

              return (
                <tbody key={group.key} className="la-group">
                  {/* Employee parent row — one-line summary per person */}

                  <tr
                    className="la-group-row"
                    onClick={() => toggleGroup(group.key)}
                  >
                    <td>
                      <div className="la-employee">
                        <button
                          type="button"
                          className="la-chevron-btn"
                          onClick={e => {
                            e.stopPropagation()
                            toggleGroup(group.key)
                          }}
                        >
                          <i
                            className={`bx ${
                              isOpen ? "bx-chevron-down" : "bx-chevron-right"
                            }`}
                          />
                        </button>

                        {group.avatar ? (
                          <img
                            src={`${configure.imgUrl}${group.avatar}`}
                            alt=""
                            className="la-avatar-img"
                          />
                        ) : (
                          <div
                            className="la-avatar-fallback"
                            style={{
                              backgroundColor: avatarColor.bg,
                              color: avatarColor.color,
                            }}
                          >
                            {getInitials(group.name)}
                          </div>
                        )}

                        <div className="la-employee-info">
                          <div className="la-emp-name">{group.name}</div>

                          <div className="la-emp-meta">
                            {[
                              group.emp_id && `ID ${group.emp_id}`,
                              group.designation,
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                          </div>

                          <div className="la-group-summary">
                            <span className="la-summary-value">
                              {group.rows.length}
                            </span>

                            <span>
                              request
                              {group.rows.length !== 1 ? "s" : ""} •{" "}
                              {group.totalDays} day
                              {group.totalDays !== 1 ? "s" : ""} total
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="la-date-cell">
                        <strong>
                          {group.periodStart
                            ? moment(group.periodStart).format("DD MMM YY")
                            : "-"}
                          {" – "}
                          {group.periodEnd
                            ? moment(group.periodEnd).format("DD MMM YY")
                            : "-"}
                        </strong>
                        <span className="la-date-muted">Overall span</span>
                      </div>
                    </td>

                    <td>
                      <div className="la-date-cell">
                        <strong>
                          {group.latestApplied
                            ? moment(group.latestApplied).format("DD MMM YYYY")
                            : "-"}
                        </strong>
                        <span className="la-date-muted">Latest applied</span>
                      </div>
                    </td>

                    <td>
                      {typeEntries.length > 0 ? (
                        <div className="la-type-chips">
                          {typeEntries.slice(0, 3).map(([type, count]) => (
                            <span key={type} className="la-type-chip" title={type}>
                              {type.length > 16
                                ? `${type.slice(0, 16)}…`
                                : type}{" "}
                              ×{count}
                            </span>
                          ))}
                          {typeEntries.length > 3 && (
                            <span className="la-muted">
                              +{typeEntries.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span
                          className="la-type-chip la-type-chip-muted"
                          title="Leave type is available inside View"
                        >
                          Leave
                        </span>
                      )}
                    </td>

                    <td>
                      <div
                        className="la-reason-cell"
                        title={latestReason || "Latest leave reason"}
                      >
                        {latestReason || (
                          <span className="la-muted">See requests below</span>
                        )}
                      </div>
                    </td>

                    <td className="center">
                      <span className="la-days-pill">
                        {group.totalDays}
                        <small>
                          {" "}
                          day
                          {group.totalDays !== 1 ? "s" : ""}
                        </small>
                      </span>
                    </td>

                    <td className="center">
                      {statusEntries.length > 0 ? (
                        <div className="la-status-counts">
                          {statusEntries.map(([status, days]) => (
                            <StatusBadge
                              key={status}
                              status={`${status} ${Number(days)}d`}
                            />
                          ))}
                        </div>
                      ) : (
                        <StatusBadge status="Pending" />
                      )}
                    </td>

                    <td className="center">
                      <span className="la-open-label">
                        {isOpen ? "Collapse" : "View requests"}
                        <i
                          className={`bx ${
                            isOpen ? "bx-chevron-up" : "bx-chevron-down"
                          }`}
                        />
                      </span>
                    </td>
                  </tr>

                  {/* Child leave rows */}

                  {isOpen &&
                    group.rows.map((row, index) => {
                      const rawType = getRowType(row)
                      const rowType = isCasualType(rawType) ? "" : rawType
                      const rowStatus = getRowStatus(row)
                      return (
                        <tr key={row.id ?? index} className="la-detail-row">
                          <td>
                            <div className="la-request-indent">
                              <span className="la-child-line" />

                              <span className="la-request-label">
                                Request{row.id ? ` #${row.id}` : ""}
                              </span>
                            </div>
                          </td>

                          <td>
                            <div className="la-date-cell">
                              <strong>
                                {formatLeaveDate(
                                  getRowStartDate(row),
                                  getRowEndDate(row)
                                )}
                              </strong>
                              <span className="la-date-muted">
                                {row.leave_days || 0} day
                                {Number(row.leave_days) !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </td>

                          <td>
                            <span className="la-date-muted">
                              {formatShortDate(getRowAppliedDate(row))}
                            </span>
                          </td>

                          <td>
                            {rowType ? (
                              <span className="la-type-chip" title={rowType}>
                                {rowType}
                              </span>
                            ) : (
                              <span
                                className="la-type-chip la-type-chip-muted"
                                title="Leave type is available inside View"
                              >
                                Leave
                              </span>
                            )}
                          </td>

                          <td>
                            <div
                              className="la-reason-cell"
                              title={row.leave_reason || ""}
                            >
                              {row.leave_reason || "No reason provided"}
                            </div>
                          </td>

                          <td className="center">
                            <span className="la-days-value">
                              {row.leave_days || 0}
                            </span>
                          </td>

                          <td className="center">
                            {rowStatus ? (
                              <StatusBadge status={rowStatus} />
                            ) : (
                              <span
                                className="la-muted"
                                title="Open View for day-wise status"
                              >
                                —
                              </span>
                            )}
                          </td>

                          <td className="center">
                            <button
                              type="button"
                              className="la-view-btn"
                              onClick={e => {
                                e.stopPropagation()

                                onLeaveDetail(row.id, row.employee, row)
                              }}
                            >
                              <i className="bx bx-show" />
                              View
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              )
            })}
          </table>
        </div>

        {/* Pagination */}
          <div className="la-pagination">
            <span className="la-page-info">
              Showing <strong>{getPageRange()}</strong> of{" "}
              <strong>{groups.length}</strong> employees
            </span>

            <div className="la-page-controls">
              <button
                type="button"
                className="la-page-btn"
                disabled={safePageIndex === 0}
                onClick={() => setPageIndex(p => Math.max(0, p - 1))}
              >
                <i className="bx bx-chevron-left" />
              </button>

              {Array.from(
                {
                  length: Math.min(pageCount, 5),
                },
                (_, index) => {
                  let page = index

                  if (pageCount > 5 && safePageIndex > 2) {
                    page = Math.min(
                      safePageIndex - 2 + index,
                      pageCount - 5 + index
                    )
                  }

                  return (
                    <button
                      key={page}
                      type="button"
                      className={`la-page-btn ${
                        page === safePageIndex ? "active" : ""
                      }`}
                      onClick={() => setPageIndex(page)}
                    >
                      {page + 1}
                    </button>
                  )
                }
              )}

              <button
                type="button"
                className="la-page-btn"
                disabled={safePageIndex >= pageCount - 1}
                onClick={() => setPageIndex(p => Math.min(pageCount - 1, p + 1))}
              >
                <i className="bx bx-chevron-right" />
              </button>
            </div>
          </div>
        </div>
    </div>
  )
}

/* ---------------------------------------------------------------- */
/* Page container — data fetching / modals (logic unchanged)         */
/* ---------------------------------------------------------------- */
const RESTRICTED_STAFF_IDS = [46, 50, 168]
const DESIGNATED_APPROVER_ID = 32

const AppliedLeaves = () => {
  const currentUserId = parseInt(localStorage.getItem("auth_id"))
  const [isLoading, setLoading] = useState(false)
  const [modal_Access, setAccessDenied] = useState(false)
  const [leaveDetail, setLeaveDetails] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [addReasons, setAddReasons] = useState(false)
  const [data, setData] = useState([])
  const isApprover = currentUserId === DESIGNATED_APPROVER_ID
  const [filterMode, setFilterMode] = useState(isApprover ? "default" : "all")
  const [details, setDetails] = useState({})
  const [reason, setReason] = useState("")
  const [leaveId, setLeaveId] = useState("")
  const [staffId, setStaffId] = useState("")
  const [reasons, setReasons] = useState("")
  const [leaveSlots, setLeaveSlots] = useState([])
  const [allLeave, setAllLeave] = useState([])
  const [slotId, setSlotId] = useState("")
  const [staffDetail, setStaffDetail] = useState({})
  const [statuses, setStatuses] = useState([])
  const [statsRefreshKey, setStatsRefreshKey] = useState(0)

  const statusOptions = [
    { value: "Applied", label: "Applied" },
    { value: "Approved", label: "Approved" },
    { value: "Unapproved", label: "Unapproved" },
    { value: "Cancel", label: "Cancel" },
  ]

  useEffect(() => {
    if (statuses.length) {
      const filterSlots = allLeave.filter(slot =>
        statuses.some(item => item.value === slot.leave_status)
      )
      setLeaveSlots(filterSlots)
    } else {
      setLeaveSlots(allLeave)
    }
  }, [statuses])

  const toggleLoading = loading => setLoading(loading)

  const onLeaveDetail = async (id, staff_id, obj) => {
    const res = await fetchData(toggleLoading, `LeaveSlots/${id}`, "")
    if (res?.success) {
      setStaffDetail(obj)
      setLeaveDetails(true)
      setLeaveId(id)
      setDetails(res?.data)
      setReason(res?.data?.leaveslots[0]?.comment)
      setStatuses([])
      setLeaveSlots(res?.data?.leaveslots)
      setAllLeave(res?.data?.leaveslots)
      setStaffId(staff_id)
    }
  }

  const onChangeStatus = async (id, status, disapprove) => {
    const formEnties = {
      data_id: id,
      status: status,
      type: 1,
      leave_reason: disapprove ? reasons : "",
    }
    const res = await FetchPostData(
      toggleLoading,
      `approveList`,
      "",
      formEnties
    )
    if (res?.data?.success) {
      setAddReasons(false)
      setReasons("")
      setStatsRefreshKey(key => key + 1)
      Swal.fire({
        icon: "success",
        text: `Leave ${
          status == "Cancel" ? "Cancelled" : "Approved"
        }  Successfully`,
      }).then(async () => {
        const response = await fetchData(
          toggleLoading,
          `LeaveSlots/${leaveId}`,
          ""
        )
        if (response?.success) {
          setLeaveSlots(response?.data?.leaveslots)
          setAllLeave(response?.data?.leaveslots)
        }
      })
    }
  }

  const filteredData = useMemo(() => {
    if (!isApprover || filterMode === "all") return data
    return data.filter(row => RESTRICTED_STAFF_IDS.includes(row.employee))
  }, [data, filterMode, isApprover])

  useEffect(() => {
    const fetchLeaves = async () => {
      const res = await fetchData(toggleLoading, `approvalList/${1}`, "")
      if (res?.success) setData(res?.data)
    }
    fetchLeaves()
  }, [])

  const onGetHisory = async () => {
    const res = await fetchData(toggleLoading, `LeaveHistory/${staffId}`, "")
    if (res?.success) {
      setLeaveSlots(res?.data?.leaveslots)
      setShowHistory(true)
    }
  }

  return (
    <div>
      <React.Fragment>
        {isLoading && <Loader />}
        <div className="position-relative">
          <Row>
            <Col lg="12">
              <GroupedLeavesTable
                data={filteredData}
                onLeaveDetail={onLeaveDetail}
                filterMode={filterMode}
                setFilterMode={setFilterMode}
                isApprover={isApprover}
              />
            </Col>
          </Row>
        </div>

        <Modal isOpen={addReasons} size="md" centered>
          <div className="leave-approvals modal-header pe-4">
            <h5 className="modal-title mt-0">Add Reason</h5>
            <button
              type="button"
              onClick={() => setAddReasons(false)}
              className="btn-modal-close"
            >
              <span aria-hidden="true" className="btn-modal-span">
                &times;
              </span>
            </button>
          </div>
          <div className="leave-approvals modal-body pe-4">
            <form
              onSubmit={e => {
                e.preventDefault()
                onChangeStatus(slotId, "Unapproved", true)
              }}
            >
              <div className="col-md-12">
                <label>Reason</label>
                <textarea
                  className="form-control"
                  placeholder="Reason for Disapproval"
                  value={reasons}
                  onChange={e => setReasons(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-3 mt-3">
                <button className="btn btn-save btn-label">
                  <i className="fas fa-save me-2 label-icon" /> save
                </button>
              </div>
            </form>
          </div>
        </Modal>

        <Modal isOpen={leaveDetail} size="xl" className="" centered>
          <div className="modal-header pe-4">
            <div className="d-flex gap-3 col-md-9 justify-content-between">
              <h5 className="modal-title mt-0">Leave Details</h5>
              <div className="d-flex gap-3">
                <h5 className="text-purple fw-bold mb-0">
                  <i className="bx bxs-user me-2"></i> {staffDetail?.name}
                </h5>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLeaveDetails(false)}
              className="btn-modal-close"
            >
              <span aria-hidden="true" className="btn-modal-span">
                &times;
              </span>
            </button>
          </div>

          <div className="modal-body pe-4 my-3">
            <Form className="row d-flex justify-content-center align-items-center">
              <div className="col-md-5 d-flex flex-column justify-content-end align-items-center ps-3 gap-4">
                <div className="text-center">
                  <img src={leaveImg2} alt="Addepe" style={{ width: "70%" }} />
                </div>
              </div>

              <div className="col-md-7 d-flex flex-column justify-content-start gap-3">
                <div className="col-md-12 d-flex flex-row mb-3 mt-2 gap-3">
                  <div className="col-md-12 d-flex justify-content-start align-items-center gap-3">
                    <h5 className="mb-0">Leave Balance :</h5>
                    <div className="d-flex justify-content-between align-items-center gap-2 col-md-9 flex-wrap">
                      <div className="d-flex gap-2 justify-content-start align-items-center flex-wrap">
                        <span
                          className="mb-0"
                          style={{
                            padding: "5px 12px",
                            borderRadius: "999px",
                            background: "#eaf2ff",
                            color: "#245fbd",
                            border: "1px solid #245fbd30",
                            fontSize: "12px",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <b>
                            EL :{" "}
                            <span>
                              {details?.earned_leave} ({details?.ael})
                            </span>
                          </b>
                        </span>
                        <span
                          className="mb-0"
                          style={{
                            padding: "5px 12px",
                            borderRadius: "999px",
                            background: "#fff3df",
                            color: "#a95709",
                            border: "1px solid #a9570930",
                            fontSize: "12px",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <b>
                            SL :{" "}
                            <span>
                              {details?.sick_leave} ({details?.asl})
                            </span>
                          </b>
                        </span>
                      </div>
                      <div className="d-flex gap-2 align-items-center">
                        <span
                          className="mb-0"
                          style={{
                            padding: "5px 12px",
                            borderRadius: "999px",
                            background: "#f1e6f8",
                            color: "#5b2178",
                            border: "1px solid #752a9730",
                            fontSize: "12px",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <b>
                            Total : <span>{allLeave?.length}</span>
                          </b>
                        </span>
                        <span
                          className="mb-0 cursor-pointer"
                          style={{
                            padding: "5px 11px",
                            borderRadius: "999px",
                            background: "#e8f7ee",
                            color: "#087a42",
                            border: "1px solid #087a4230",
                            fontSize: "12px",
                            fontWeight: 700,
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                          }}
                          onClick={() => {
                            if (showHistory) {
                              setLeaveSlots(allLeave)
                              setShowHistory(false)
                            } else {
                              onGetHisory()
                            }
                          }}
                        >
                          <b>
                            <i
                              className={
                                showHistory ? "fab fa-rev" : "fa fa-history"
                              }
                            ></i>
                          </b>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <LeaveStatsSummary
                  userId={staffId}
                  months={6}
                  refreshKey={statsRefreshKey}
                />

                {showHistory == false && (
                  <div className="col-12 mb-3">
                    <label className="">Reasons</label>
                    <textarea
                      className="form-control"
                      value={reason}
                      disabled
                    />
                  </div>
                )}

                <div className="d-flex flex-column gap-1">
                  <h5 className="mb-0 ps-1 mb-2 d-flex justify-content-between">
                    {showHistory ? "Leave History" : "Leave Slots"} :{" "}
                    <div className=" ">
                      <Select
                        placeholder="Filter By Status"
                        className="basic-multi-select fs-6"
                        classNamePrefix=" select"
                        isMulti={true}
                        options={statusOptions}
                        value={statuses}
                        onChange={e => setStatuses(e)}
                      />
                    </div>
                  </h5>
                  <div
                    className={`table-responsive col-md-12 ${
                      showHistory
                        ? "leave-history-slots"
                        : "leave-history-table"
                    }`}
                  >
                    <Table className="table mb-0">
                      <thead
                        className="bg-white"
                        style={{ position: "sticky", top: 0, zIndex: 2 }}
                      >
                        <tr className=" ">
                          <th className="header text-center col-1">
                            Leave Date
                          </th>
                          <th className="header col-1">Type</th>
                          <th className="header col-1">Status</th>
                          <th
                            className="text-center header"
                            style={{ width: "10%" }}
                          >
                            {showHistory ? "Reason" : "Action"}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="">
                        {leaveSlots?.map((obj, i) => {
                          const currentUserId = parseInt(
                            localStorage.getItem("auth_id")
                          )
                          const isRestricted = RESTRICTED_STAFF_IDS.includes(
                            parseInt(staffId)
                          )
                          const canApprove =
                            !isRestricted ||
                            currentUserId === DESIGNATED_APPROVER_ID
                          return (
                            <tr key={i}>
                              <th scope="row" className="text-center">
                                {moment(obj?.leave_dates).format("DD-MM-YYYY")}
                              </th>
                              <td>
                                <span className="la-type-chip">
                                  {obj?.leave_name || "Leave"}
                                </span>{" "}
                                {obj?.medical_certificate != null ? (
                                  <a
                                    className="ms-1"
                                    href={obj?.medical_certificate}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <i className="fa fa-download" />
                                  </a>
                                ) : null}
                              </td>
                              <td className="text-center">
                                {obj?.leave_status ? (
                                  <StatusBadge status={obj.leave_status} />
                                ) : (
                                  <StatusBadge status="Pending" />
                                )}
                              </td>
                              <td
                                className="word-wrap text-center"
                                style={{ width: "10%" }}
                              >
                                {showHistory ? (
                                  obj?.comment || "-"
                                ) : (
                                  <>
                                    {obj?.status != 1 ? (
                                      canApprove ? (
                                        <>
                                          <a
                                            className="btn btn-sm btn-outline-primary me-1 lh-1 p-1"
                                            title="Approve"
                                            onClick={() =>
                                              onChangeStatus(
                                                obj?.slots_id,
                                                "Approved"
                                              )
                                            }
                                          >
                                            <i className="bi bi-check-lg icon-center"></i>
                                          </a>
                                          <a
                                            className="btn btn-sm btn-outline-danger me-1 lh-1 p-1"
                                            title="Disapprove"
                                            onClick={() => {
                                              setSlotId(obj?.slots_id)
                                              setAddReasons(true)
                                            }}
                                          >
                                            <i className="dripicons-cross icon-center"></i>
                                          </a>
                                          <a
                                            className="btn btn-sm btn-outline-secondary me-1 lh-1 p-1"
                                            title="Cancel"
                                            onClick={() =>
                                              onChangeStatus(
                                                obj?.slots_id,
                                                "Cancel"
                                              )
                                            }
                                          >
                                            <i className="fas fa-times icon-center"></i>
                                          </a>
                                        </>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )
                                    ) : (
                                      <>{obj?.leave_reason || "-"}</>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </Modal>

        <AccessDeniedPopup
          modal_Access={modal_Access}
          setAccessDenied={setAccessDenied}
        />
      </React.Fragment>
    </div>
  )
}

export default AppliedLeaves
